import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { FlexColumn, Header2, Loader } from '@ubeya/shared-web/components';
import { AccordionSummaryContainer } from '@ubeya/shared-web/components/Accordion/Accordion';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useEmployeeContracts from '@ubeya/shared/hooks/employees/useEmployeeContracts';
import useContracts from '@ubeya/shared/hooks/account/useContracts';
import { RootWrapper } from '../styled';
import EmployeeContractAssign from './EmployeeContractAssign';
import ActiveContract from './ActiveContract';
import ContractEmptyState from './ContractEmptyState';
import EmployeeContractCreate from './EmployeeContractCreate';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 0 41px 40px;
  min-width: 500px;
  gap: 16px;
`;
const ContentContainer = styled(FlexColumn)`
  gap: 16px;
`;

const EmployeeContracts = ({ accountRequiresContract, data: employeeData, mode }) => {
  const { id: employeeId } = employeeData || {};
  const { t } = useContext(ConfigContext);

  const {
    data: contracts,
    isLoading,
    assignContract,
    terminateEmployeeContract,
    adjustEmployeeContract
  } = useEmployeeContracts({
    employeeId
  });
  const { contracts: accountContracts, mappedContracts } = useContracts();

  const isAddMode = mode === 'ADD';

  const activeContract = useMemo(() => {
    const activeEmployeeContract = (contracts || []).find((contract) => contract.isActive);

    if (!activeEmployeeContract) return null;
    const {
      id,
      startDate,
      holidayAccrualYearlyEntitlement,
      maxDaysConsumeInWeek,
      minimumWeeklyWorkingHours,
      minimumHourlyRateOfPay,
      sickDaysYearlyEntitlement,
      holidayAccrualYearlyEntitlementAdjustment,
      sickDaysYearlyEntitlementAdjustment,
      hoursPerWorkday: employeeHoursPerWorkday,
      employmentRate
    } = activeEmployeeContract;

    return {
      ...mappedContracts[activeEmployeeContract?.accountContractId],
      employeeStartDate: startDate,
      employeeHolidayAccrualYearlyEntitlement: holidayAccrualYearlyEntitlement,
      employeeMaxDaysConsumeInWeek: maxDaysConsumeInWeek,
      employeeMinimumWeeklyWorkingHours: minimumWeeklyWorkingHours,
      employeeMinimumHourlyRateOfPay: minimumHourlyRateOfPay,
      employeeSickDaysYearlyEntitlement: sickDaysYearlyEntitlement,
      holidayAccrualYearlyEntitlementAdjustment,
      sickDaysYearlyEntitlementAdjustment,
      employeeHoursPerWorkday,
      employmentRate,
      employeeContractId: id
    };
  }, [contracts, mappedContracts]);

  const noActiveContract = !activeContract && !isLoading;

  return (
    <RootWrapper>
      <Wrapper>
        <AccordionSummaryContainer>
          <Header2> {t('contracts')} </Header2>
        </AccordionSummaryContainer>
        <ContentContainer>
          {isAddMode ? (
            <EmployeeContractCreate
              accountContracts={accountContracts}
              accountRequiresContract={accountRequiresContract}
            />
          ) : isLoading ? (
            <Loader />
          ) : (
            <>
              <EmployeeContractAssign
                activeContract={activeContract}
                accountContracts={accountContracts}
                assignContract={assignContract}
              />
              {noActiveContract && <ContractEmptyState />}
              {activeContract && (
                <ActiveContract
                  activeContract={activeContract}
                  terminateEmployeeContract={terminateEmployeeContract}
                  adjustEmployeeContract={adjustEmployeeContract}
                />
              )}
            </>
          )}
        </ContentContainer>
      </Wrapper>
    </RootWrapper>
  );
};

export default EmployeeContracts;
